import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig
} from 'axios'

import { analyticsDistinctId } from 'src/analytics/identity'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from 'src/constants'
import {
  clearLocalStorage,
  getLocalStorageTokens,
  getRefreshToken
} from 'src/utils/getToken'

type Client = {
  instance: AxiosInstance
}

type InterceptorOptions = {
  getSaasAppToken?: () => string | undefined
}

type RetryRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

const initializedClients = new WeakSet<AxiosInstance>()

const PUBLIC_AUTH_ENDPOINTS = [
  '/users/register/',
  '/users/register/organization/',
  '/authorization/token/client/',
  '/authorization/token/',
  // MFA step endpoints carry their own short-lived mfa_access_token in the
  // Authorization header, so the session-token interceptor must leave them
  // alone (and the response interceptor must not try to refresh-retry them).
  '/mfa/send-otp/',
  '/mfa/verify-otp/',
  '/mfa/verify-backup-code/',
  '/authorization/qr/start/',
  '/authorization/qr/wait/',
  '/authorization/qr/cancel/',
  '/authorization/social/google/start/client/',
  '/authorization/social/exchange/client/',
  '/authorization/social/google/state-info/',
  '/authorization/social/entraid/state-info/',
  '/authorization/client/auth-methods/',
  '/authorization/v2/password/request/',
  '/authorization/v2/password/reset/',
  '/authorization/v2/signup/start/',
  '/authorization/v2/signup/confirm/',
  '/organizations/info/',
  '/organizations/domain-availability/'
]

const COOKIE_ENDPOINT = '/authorization/token/cookie/'

const getRequestPath = (request?: InternalAxiosRequestConfig): string => {
  if (!request?.url) {
    return ''
  }

  try {
    const parsedUrl = new URL(
      request.url,
      request.baseURL || window.location.origin
    )
    return parsedUrl.pathname
  } catch {
    return request.url
  }
}

const isPublicAuthRequest = (request?: InternalAxiosRequestConfig) => {
  const path = getRequestPath(request)
  return PUBLIC_AUTH_ENDPOINTS.some((endpoint) => path.startsWith(endpoint))
}

const isCookieAuthRequest = (request?: InternalAxiosRequestConfig) => {
  const path = getRequestPath(request)
  return path.startsWith(COOKIE_ENDPOINT)
}

export const getConfigInterceptors = (
  axios: Client['instance'],
  options?: InterceptorOptions
) => {
  if (!axios || initializedClients.has(axios)) {
    return
  }
  initializedClients.add(axios)

  axios.interceptors.request.use(
    (request: InternalAxiosRequestConfig) => {
      const { access } = getLocalStorageTokens()
      // Never clobber an Authorization header a caller set explicitly (e.g. the
      // MFA / email-OTP flows attach their own short-lived token). Only inject
      // the stored session token when the request doesn't already carry one.
      if (
        access &&
        !request.headers.has('Authorization') &&
        !isPublicAuthRequest(request)
      ) {
        request.headers['Authorization'] = `Token ${access}`
      }

      const clientScopedSaasAppToken = options?.getSaasAppToken?.()
      if (clientScopedSaasAppToken && !request.headers.has('Saas-App-Token')) {
        request.headers['Saas-App-Token'] = clientScopedSaasAppToken
      }

      // Who this browser is before it is anybody. The platform stamps its own
      // pre-signup events with it, and `identify(userId)` after registration is
      // what folds those events onto the account — without the header the two
      // halves of the funnel stay two anonymous strangers. Only while there is
      // no session: once the request carries an account, the account *is* the
      // identity and a second one would only invite them to disagree.
      if (!access) {
        const analyticsId = analyticsDistinctId()
        if (analyticsId) request.headers['X-Analytics-Id'] = analyticsId
      }

      return request
    },
    (error) => Promise.reject(error)
  )

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryRequestConfig | undefined
      if (!originalRequest) {
        return Promise.reject(error)
      }

      const isAuthError =
        error.response?.status === 401 || error.response?.status === 403

      if (isPublicAuthRequest(originalRequest)) {
        return Promise.reject(error)
      }

      if (isCookieAuthRequest(originalRequest) && isAuthError) {
        return Promise.reject(error)
      }

      const isInvalidToken =
        (error.response?.data as { code?: string } | undefined)?.code ===
        'token_not_valid'

      if (isAuthError && isInvalidToken && !originalRequest._retry) {
        originalRequest._retry = true

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        if (!refreshToken) {
          clearLocalStorage()
          return Promise.reject(error)
        }

        try {
          const refreshResult = await getRefreshToken(refreshToken)
          const access = refreshResult?.access
          if (access) {
            localStorage.setItem(ACCESS_TOKEN_KEY, access)
            originalRequest.headers['Authorization'] = `Token ${access}`
            return axios(originalRequest)
          }

          clearLocalStorage()
          return Promise.reject(error)
        } catch (refreshError) {
          clearLocalStorage()
          return Promise.reject(refreshError)
        }
      }

      return Promise.reject(error)
    }
  )
}
